import React, { useEffect } from 'react';
import { BarChart3, PlayCircle, ArrowRight } from 'lucide-react';
import { useStudentHome } from './useStudentHome';
import Spinner from '@/components/common/spinner/Spinner';
import AlertMessage from '@/components/layout/message/AlertMessage';
import styles from './StudentHome.module.css';
import AppHeader from '@/components/layout/header/AppHeader';
import PageLayout from '@/components/layout/pageLayout/PageLayout';
import Card from '@/components/common/card/Card';
import StudentHeroArt from './components/StudentHeroArt';

const StudentHome: React.FC = () => {
    const {
        isLoading,
        error,
        user,
        hasVirtualSimulations,
        handleNavigateToDashboard,
        handleNavigateToVirtual,
    } = useStudentHome();

    useEffect(() => {
        document.body.classList.add('solid-public-bg');
        return () => document.body.classList.remove('solid-public-bg');
    }, []);

    if (isLoading) {
        return <Spinner text="Cargando tu información..." />;
    }

    if (error) {
        return <AlertMessage type="error" title="Error">{error.generalMessage}</AlertMessage>;
    }

    const mainContent = (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroText}>
                    <span className={styles.kicker}>Tu espacio de estudio</span>
                    <h1 className={styles.title}>
                        ¡Bienvenido de vuelta, {user?.first_name}!
                    </h1>
                    <p className={styles.subtitle}>
                        Presenta simulacros tipo ICFES, revisa tus resultados y sigue tu
                        progreso. ¿Qué te gustaría hacer hoy?
                    </p>
                </div>
                <div className={styles.heroArt}>
                    <StudentHeroArt />
                </div>
            </section>

            <section className={styles.actions}>
                <div className={styles.cardsGrid}>
                    <Card className={`${styles.homeCard} ${styles.dashboardCard}`}>
                        <button className={styles.cardButton} onClick={handleNavigateToDashboard}>
                            <div className={styles.cardIconWrapper}>
                                <BarChart3 size={30} className={styles.cardIcon} />
                            </div>
                            <div className={styles.cardBody}>
                                <h2 className={styles.cardTitle}>Dashboard de resultados</h2>
                                <p className={styles.cardDescription}>
                                    Revisa tus estadísticas, percentiles y el progreso de tus
                                    simulacros anteriores.
                                </p>
                            </div>
                            <div className={styles.cardAction}>
                                <span>Ver resultados</span>
                                <ArrowRight size={18} className={styles.cardArrow} />
                            </div>
                        </button>
                    </Card>

                    {hasVirtualSimulations && (
                        <Card className={`${styles.homeCard} ${styles.simulacroCard}`}>
                            <button className={styles.cardButton} onClick={handleNavigateToVirtual}>
                                <div className={styles.cardIconWrapper}>
                                    <PlayCircle size={30} className={styles.cardIcon} />
                                </div>
                                <div className={styles.cardBody}>
                                    <h2 className={styles.cardTitle}>Presentar simulacro</h2>
                                    <p className={styles.cardDescription}>
                                        Pon a prueba tus conocimientos con un nuevo simulacro
                                        virtual.
                                    </p>
                                </div>
                                <div className={styles.cardAction}>
                                    <span>Comenzar ahora</span>
                                    <ArrowRight size={18} className={styles.cardArrow} />
                                </div>
                            </button>
                        </Card>
                    )}
                </div>
            </section>
        </div>
    );

    return <PageLayout header={<AppHeader />} main={mainContent} />;
};

export default StudentHome;
